import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDuration, isExerciseCompleteForOrdering, stablePartitionCompletedFirst } from '@liftx/core';
import { Screen } from '../../ui/components/Screen';
import { ExercisePickerDialog } from '../../ui/components/ExercisePickerDialog';
import { sessionRepository } from '../../data/repositories/sessionRepository';
import { ROUTES, workoutSummaryPath } from '../../ui/navigation/routes';
import { useWakeLock } from './useWakeLock';
import { useElapsedTimer } from './useElapsedTimer';
import { ExercisePanel } from './ExercisePanel';
import { CardioPanel } from './CardioPanel';
import { RestTimerBar } from './RestTimerBar';
import { AbandonConfirmDialog } from './AbandonConfirmDialog';
import { RemoveExerciseDialog } from './RemoveExerciseDialog';
import type { ExerciseRecord, SessionBlockRecord, SessionExerciseRecord, SessionRecord } from '../../data/types';
import styles from './Workout.module.css';

const DEFAULT_REST_SECONDS = 60;

interface BlockWithExercises {
  block: SessionBlockRecord;
  exercises: SessionExerciseRecord[];
}

/**
 * The live execution screen. Master Phase 6.
 *
 * Every meaningful action here is a direct `sessionRepository` write on the
 * action itself — nothing that represents progress waits for "save" (§11 of
 * the approved plan). What *is* only React state: which exercise panel is
 * currently expanded — a reload recomputes that from persisted completion
 * status, never loses it.
 */
export function ActiveWorkoutScreen(): React.JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionRecord | null | undefined>(undefined);
  const [blocks, setBlocks] = useState<BlockWithExercises[]>([]);
  const [expandedExerciseId, setExpandedExerciseId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [confirmingAbandon, setConfirmingAbandon] = useState(false);
  const [removing, setRemoving] = useState<{ exerciseId: string; hasCompletedWork: boolean } | null>(null);
  const elapsedSeconds = useElapsedTimer(session?.startedAt ?? 0, session?.endedAt ?? null);

  const refresh = useCallback(async () => {
    if (sessionId === undefined) return;
    const record = await sessionRepository.getSession(sessionId);
    if (record === undefined) {
      setSession(null);
      return;
    }
    setSession(record);

    const blockRows = await sessionRepository.listBlocks(sessionId);
    const withExercises = await Promise.all(
      blockRows.map(async (block) => ({ block, exercises: await sessionRepository.listExercises(block.id) })),
    );

    // Auto-expand the first exercise with outstanding work, the first time
    // we see it — never overrides a choice the user already made by tapping
    // a different panel. Deliberately walks the SESSION's own persisted
    // order, not the completed-first display order computed below: this
    // pick is about "what's next in the workout," which must not change
    // just because an earlier exercise finishing moved it up the display.
    setExpandedExerciseId((current) => {
      // A stale id (its exercise was just removed) is treated as "nothing
      // chosen", so the pick below falls to the next exercise with work
      // outstanding — by stable id, never by array position.
      const stillThere = withExercises.some(({ exercises }) => exercises.some((exercise) => exercise.id === current));
      if (current !== null && stillThere) return current;
      for (const { exercises } of withExercises) {
        for (const exercise of exercises) {
          if (exercise.status !== 'skipped' && exercise.status !== 'completed') return exercise.id;
        }
      }
      return withExercises[0]?.exercises[0]?.id ?? null;
    });

    // Completed-first display ordering (Phase 3) — purely a render-time
    // transform of the array handed to `blocks` state below. Nothing here
    // writes to `SessionExerciseRecord.orderIndex`, the Training Plan's own
    // order, or any new field: every refresh re-derives the same order from
    // whatever is currently live/completed, so a reload or a set later being
    // uncompleted/re-edited is reflected automatically, with nothing to
    // "remember." Block grouping and block order are untouched — only the
    // exercises *within* each block are reordered.
    const displayBlocks = await Promise.all(
      withExercises.map(async ({ block, exercises }) => {
        const withCompletion = await Promise.all(
          exercises.map(async (exercise) => {
            const liveRows =
              exercise.modality === 'cardio'
                ? await sessionRepository.listSegments(exercise.id)
                : await sessionRepository.listSets(exercise.id);
            return { exercise, complete: isExerciseCompleteForOrdering(exercise.status, liveRows) };
          }),
        );
        return {
          block,
          exercises: stablePartitionCompletedFirst(withCompletion, (row) => row.complete).map((row) => row.exercise),
        };
      }),
    );
    setBlocks(displayBlocks);
  }, [sessionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useWakeLock(session?.status === 'active');

  useEffect(() => {
    if (session?.status === 'completed') {
      navigate(workoutSummaryPath(session.id), { replace: true });
    }
  }, [session, navigate]);

  async function handlePause(): Promise<void> {
    if (sessionId === undefined) return;
    await sessionRepository.pause(sessionId);
    await refresh();
  }

  async function handleResume(): Promise<void> {
    if (sessionId === undefined) return;
    await sessionRepository.resume(sessionId);
    await refresh();
  }

  async function handleAbandon(): Promise<void> {
    if (sessionId === undefined) return;
    setConfirmingAbandon(false);
    // Abandoning discards the workout outright — it must never become a
    // recorded session. `deleteSession` tombstones the entire session tree
    // (blocks, exercises, sets, cardio segments) in one transaction, which is
    // exactly "not saved to history": the same cascade History's own Delete
    // action uses, just invoked before the workout was ever finished.
    await sessionRepository.deleteSession(sessionId);
    navigate(ROUTES.workout, { replace: true });
  }

  async function handleFinish(): Promise<void> {
    if (sessionId === undefined) return;
    setBusy(true);
    await sessionRepository.complete(sessionId);
    setBusy(false);
    navigate(workoutSummaryPath(sessionId));
  }

  async function handleSetCompleted(block: SessionBlockRecord, exercise: SessionExerciseRecord): Promise<void> {
    if (sessionId === undefined) return;
    const restSeconds = block.restSeconds ?? exercise.restSeconds ?? DEFAULT_REST_SECONDS;
    if (restSeconds > 0) {
      await sessionRepository.startRestTimer(sessionId, block.id, restSeconds);
    }
    await refresh();
  }

  /** Opens the confirmation; "completed work" decides which warning copy shows. */
  async function handleRequestRemove(exercise: SessionExerciseRecord): Promise<void> {
    const rows =
      exercise.modality === 'cardio'
        ? await sessionRepository.listSegments(exercise.id)
        : await sessionRepository.listSets(exercise.id);
    setRemoving({ exerciseId: exercise.id, hasCompletedWork: rows.some((row) => row.isCompleted) });
  }

  async function handleConfirmRemove(): Promise<void> {
    if (removing === null) return;
    const { exerciseId } = removing;
    setRemoving(null);
    await sessionRepository.removeExercise(exerciseId);
    await refresh();
  }

  async function handleAddExercise(exercise: ExerciseRecord): Promise<void> {
    setPicking(false);
    // Appends to the last block — the only block a free workout ever has,
    // and the natural "add it at the end" spot for a plan-based session too.
    const lastBlock = blocks[blocks.length - 1]?.block;
    if (lastBlock === undefined) return;
    await sessionRepository.addExercise(lastBlock.id, exercise.id);
    await refresh();
  }

  async function handleAdjustRest(deltaS: number): Promise<void> {
    if (sessionId === undefined) return;
    await sessionRepository.adjustRestTimer(sessionId, deltaS);
    await refresh();
  }

  async function handleSkipRest(): Promise<void> {
    if (sessionId === undefined) return;
    await sessionRepository.skipRestTimer(sessionId);
    await refresh();
  }

  async function handleStartHoldTimer(sessionSetId: string, durationS: number): Promise<void> {
    if (sessionId === undefined) return;
    await sessionRepository.startHoldTimer(sessionId, sessionSetId, durationS);
    await refresh();
  }

  async function handleClearHoldTimer(): Promise<void> {
    if (sessionId === undefined) return;
    await sessionRepository.clearHoldTimer(sessionId);
    await refresh();
  }

  if (sessionId === undefined || session === null) {
    return (
      <p role="status" className={styles.stateText}>
        This workout could not be found.
      </p>
    );
  }
  if (session === undefined) {
    return (
      <p role="status" className={styles.stateText}>
        Loading workout…
      </p>
    );
  }

  return (
    <Screen title={session.name ?? 'Workout'} {...(session.status === 'paused' ? { subtitle: 'Paused' } : {})}>
      <div className={styles.elapsedTimer}>
        <span className={styles.elapsedTimerLabel}>Workout time</span>
        <span className={styles.elapsedTimerValue}>{formatDuration(elapsedSeconds)}</span>
      </div>

      {session.activeRestTimer !== null && (
        <RestTimerBar
          timer={session.activeRestTimer}
          onAdjust={(delta) => void handleAdjustRest(delta)}
          onSkip={() => void handleSkipRest()}
        />
      )}

      <div className={styles.actions}>
        {session.status === 'active' && (
          <button type="button" className={styles.actionButton} onClick={() => void handlePause()}>
            Pause
          </button>
        )}
        {session.status === 'paused' && (
          <button type="button" className={styles.actionButton} onClick={() => void handleResume()}>
            Resume
          </button>
        )}
        <button type="button" className={`${styles.actionButton} ${styles.actionDanger}`} onClick={() => setConfirmingAbandon(true)}>
          Abandon
        </button>
      </div>

      {picking && (
        <ExercisePickerDialog onSelect={(row) => void handleAddExercise(row)} onCancel={() => setPicking(false)} />
      )}

      {confirmingAbandon && (
        <AbandonConfirmDialog onConfirm={() => void handleAbandon()} onCancel={() => setConfirmingAbandon(false)} />
      )}

      {removing !== null && (
        <RemoveExerciseDialog
          hasCompletedWork={removing.hasCompletedWork}
          onConfirm={() => void handleConfirmRemove()}
          onCancel={() => setRemoving(null)}
        />
      )}

      {/* A block emptied by removals stays in storage (a free workout's only
          block is where "Add exercise" attaches) but renders nothing — no
          stray wrapper or "Superset"/"Circuit" label over nothing. */}
      {blocks.filter(({ exercises }) => exercises.length > 0).map(({ block, exercises }) => (
        <div key={block.id} className={styles.block}>
          {block.kind !== 'single' && (
            <p className={styles.blockLabel}>{block.kind === 'superset' ? 'Superset' : 'Circuit'}</p>
          )}
          {exercises.map((exercise, index) => {
            const shared = {
              index,
              expanded: expandedExerciseId === exercise.id,
              onToggleExpand: () =>
                setExpandedExerciseId((current) => (current === exercise.id ? null : exercise.id)),
              onRemove: () => void handleRequestRemove(exercise),
            };

            // Cardio Phase C4. The one branch at THIS level: cardio logs
            // segments (`CardioPanel`), everything else logs sets
            // (`ExercisePanel`). Hold still materialises to ordinary sets
            // (C3) and stays on the `ExercisePanel` path — its own
            // duration/countdown behaviour (Plank / Hold Exercise Countdown
            // Timer feature) branches one level down, per set, inside
            // `ExercisePanel` itself (`HoldSetRow` vs `SetRow`), which is why
            // only `ExercisePanel` needs the Hold-timer props below.
            //
            // Rest is deliberately shared rather than given cardio its own
            // rule: `handleSetCompleted` resolves block rest → exercise rest
            // → default, none of which is strength-specific, and resting
            // after an interval is exactly as meaningful as after a set.
            return exercise.modality === 'cardio' ? (
              <CardioPanel
                key={exercise.id}
                exercise={exercise}
                {...shared}
                onSegmentCompleted={() => void handleSetCompleted(block, exercise)}
              />
            ) : (
              <ExercisePanel
                key={exercise.id}
                exercise={exercise}
                sessionId={session.id}
                {...shared}
                onSetCompleted={() => void handleSetCompleted(block, exercise)}
                activeHoldTimer={session.activeHoldTimer ?? null}
                onStartHoldTimer={(setId, durationS) => void handleStartHoldTimer(setId, durationS)}
                onClearHoldTimer={() => void handleClearHoldTimer()}
              />
            );
          })}
        </div>
      ))}

      {/* Add Exercise and Finish are placed after the exercise list, not at
          the top: they're the two actions a lifter reaches for once they've
          seen what's already in the workout, not before. `AppShell`'s main
          content area already carries bottom padding that clears the fixed
          tab bar and the safe-area inset, so this needs no bar-specific CSS
          of its own to stay reachable. */}
      <div className={styles.bottomActions}>
        <button type="button" className={styles.actionButton} onClick={() => setPicking(true)}>
          + Add exercise
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.actionOn}`}
          disabled={busy}
          onClick={() => void handleFinish()}
        >
          Finish workout
        </button>
      </div>
    </Screen>
  );
}
